import React, { createContext, useContext, useEffect, useState } from "react";

import {
  getConsent,
  setConsent,
  updateConsent,
  clearConsent,
  defaultConsent,
  hasConsented,
} from "./cookieUtils";

/**
 * Create Context
 */
const ConsentContext = createContext();

/**
 * Provider Component
 */
export const ConsentProvider = ({ children }) => {
  const [consent, setConsentState] = useState(defaultConsent);
  const [isBannerVisible, setBannerVisible] = useState(false);
  const [isLoaded, setIsLoaded] = useState(false);

  /**
   * Load consent on app start
   */
  useEffect(() => {
    const storedConsent = getConsent();

    if (storedConsent) {
      setConsentState(storedConsent);
      setBannerVisible(false);
    } else {
      setConsentState(defaultConsent);
      setBannerVisible(true);
    }

    setIsLoaded(true);
  }, []);

  /**
   * Accept all cookies
   */
  const acceptAll = () => {
    const fullConsent = {
      necessary: true,
      analytics: true,
      marketing: true,
      preferences: true,
    };

    setConsent(fullConsent);
    setConsentState({
      ...fullConsent,
      consentedAt: new Date().toISOString(),
    });

    setBannerVisible(false);
  };

  /**
   * Reject all optional cookies
   */
  const rejectAll = () => {
    const rejectedConsent = {
      necessary: true,
      analytics: false,
      marketing: false,
      preferences: false,
    };

    setConsent(rejectedConsent);
    setConsentState({
      ...rejectedConsent,
      consentedAt: new Date().toISOString(),
    });

    setBannerVisible(false);
  };

  /**
   * Save custom preferences
   */
  const savePreferences = (preferences) => {
    const updated = {
      ...defaultConsent,
      ...preferences,
      necessary: true,
    };

    updateConsent(updated);

    setConsentState({
      ...updated,
      consentedAt: new Date().toISOString(),
    });

    setBannerVisible(false);
  };

  /**
   * Reset consent (for settings page)
   */
  const resetConsent = () => {
    clearConsent();
    setConsentState(defaultConsent);
    setBannerVisible(true);
  };

  /**
   * Context value
   */
  const value = {
    consent,
    isBannerVisible,
    isLoaded,

    acceptAll,
    rejectAll,
    savePreferences,
    resetConsent,

    hasConsented: hasConsented(),
  };

  return (
    <ConsentContext.Provider value={value}>{children}</ConsentContext.Provider>
  );
};

/**
 * Hook for consuming context
 */
export const useConsent = () => {
  const context = useContext(ConsentContext);

  if (!context) {
    throw new Error("useConsent must be used within a ConsentProvider");
  }

  return context;
};
